import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import BankDetailsApi from "@apis/bankDetails";
import Fields from "@components/fields";
import Typography from "@components/typography";
import Button from "@components/button";
import FieldWrapper from "@components/wrapper/field-wrapper";
import Skeleton from "@components/skeleton";
import * as Yup from "yup";

const Container = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
`;

const Header = styled.div`
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 16px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 8px;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  border: none;
  background: ${({ active }) => (active ? "#3b82f6" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#64748b")};
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ active }) => (active ? "#3b82f6" : "#f1f5f9")};
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightSection = styled.div`
  flex: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 12px;
`;

type PaymentType = "Bank" | "Khalti" | "Esewa" | "Fonepay";

interface BankInfo {
  bankName: string;
  address: string;
  name: string;
  number: string;
}

interface DigitalWalletInfo {
  name: string;
  number: string;
}

interface BankFormValues {
  type: "Bank";
  infos: BankInfo;
  qrCode: any;
}

interface DigitalWalletFormValues {
  type: "Khalti" | "Esewa" | "Fonepay";
  infos: DigitalWalletInfo;
  qrCode: any;
}

type FormValues = BankFormValues | DigitalWalletFormValues;

const defaultValues: Record<PaymentType, FormValues> = {
  Bank: {
    type: "Bank",
    infos: { bankName: "", address: "", name: "", number: "" },
    qrCode: "",
  },
  Khalti: {
    type: "Khalti",
    infos: { name: "", number: "" },
    qrCode: "",
  },
  Esewa: {
    type: "Esewa",
    infos: { name: "", number: "" },
    qrCode: "",
  },
  Fonepay: {
    type: "Fonepay",
    infos: { name: "", number: "" },
    qrCode: "",
  },
};

const StoreBankDetails = ({ storeId }: { storeId: string }) => {
  const [activeTab, setActiveTab] = useState<PaymentType>("Bank");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Track which types have existing records (for update vs create)
  const [existingRecords, setExistingRecords] = useState<
    Record<string, boolean>
  >({});

  // Initial values for each payment type
  const [initialValues, setInitialValues] = useState<
    Record<PaymentType, FormValues>
  >({ ...defaultValues });

  const [activeType, setActiveType] = useState<string | null>(null);
  const [settingActive, setSettingActive] = useState(false);

  const fetchBankDetails = async () => {
    try {
      setFetching(true);
      const res = await BankDetailsApi.get({
        params: { storeId },
      });

      if (res && Array.isArray(res)) {
        const records: Record<string, boolean> = {};
        const newValues = { ...defaultValues };

        res.forEach((item: any) => {
          const type = item.type as PaymentType;
          if (type && defaultValues[type]) {
            records[type] = true;

            if (item.isActive) {
              setActiveType(type);
            }

            if (type === "Bank") {
              newValues[type] = {
                type: "Bank",
                infos: {
                  bankName: item.infos?.bankName || "",
                  address: item.infos?.address || "",
                  name: item.infos?.name || "",
                  number: item.infos?.number || "",
                },
                qrCode: item.qrCode || "",
              };
            } else {
              newValues[type] = {
                type,
                infos: {
                  name: item.infos?.name || "",
                  number: item.infos?.number || "",
                },
                qrCode: item.qrCode || "",
              };
            }
          }
        });

        setExistingRecords(records);
        setInitialValues(newValues);
      }
    } catch (err) {
      console.error("Failed to fetch bank details", err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (storeId) {
      fetchBankDetails();
    }
  }, [storeId]);

  // Validation schemas matching backend Joi structure
  const bankValidationSchema = Yup.object().shape({
    type: Yup.string().oneOf(["Bank"]).required(),
    infos: Yup.object().shape({
      bankName: Yup.string().required("Bank Name is required"),
      address: Yup.string().required("Bank Address is required"),
      name: Yup.string().required("Account Holder Name is required"),
      number: Yup.string().required("Account Number is required"),
    }),
    qrCode: Yup.mixed(),
  });

  const digitalWalletValidationSchema = Yup.object().shape({
    type: Yup.string().oneOf(["Khalti", "Esewa", "Fonepay"]).required(),
    infos: Yup.object().shape({
      name: Yup.string().required("Name is required"),
      number: Yup.string().required("Number is required"),
    }),
    qrCode: Yup.mixed(),
  });

  const handleSetActive = async () => {
    try {
      setSettingActive(true);
      await BankDetailsApi.setActive({
        data: {
          storeId,
          type: activeTab,
        },
      });
      setActiveType(activeTab);
    } catch (err: any) {
      console.error("Failed to set active bank details", err);
      toast.error(err.message || "Failed to set active payment method");
    } finally {
      setSettingActive(false);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("storeId", storeId);
      formData.append("type", values.type);
      formData.append("infos", JSON.stringify(values.infos));

      // Handle QR code file upload
      if (values.qrCode && typeof values.qrCode !== "string") {
        // @ts-ignore
        if (values.qrCode.rawFile) {
          // @ts-ignore
          formData.append("qrCode", values.qrCode.rawFile);
        } else if (values.qrCode instanceof File) {
          formData.append("qrCode", values.qrCode);
        }
      }

      const apiMethod = existingRecords[values.type]
        ? BankDetailsApi.update
        : BankDetailsApi.create;

      await apiMethod({ data: formData });

      // Mark as existing after successful save
      setExistingRecords((prev) => ({ ...prev, [values.type]: true }));

      // Refetch to sync
      fetchBankDetails();
    } catch (err: any) {
      console.error("Failed to save bank details", err);
      toast.error(err.message || "Failed to save bank details");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Container>
        <Header>
          <Skeleton width={150} height={20} radius={4} />
          <Skeleton width={300} height={14} radius={2} />
        </Header>
        <div style={{ display: "flex", gap: "20px" }}>
          <div
            style={{
              width: "50%",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <Skeleton width="100%" height={50} radius={12} />
            <Skeleton width="100%" height={50} radius={12} />
            <Skeleton width="100%" height={50} radius={12} />
            <Skeleton width="100%" height={50} radius={12} />
          </div>
          <div style={{ width: "50%" }}>
            <Skeleton width="100%" height={300} radius={12} />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Typography weight={700} size={20}>
          Payment Methods
        </Typography>
        <Typography size={14} color="#64748b">
          Manage payment information for this store
        </Typography>
      </Header>

      <TabsContainer>
        <Tab active={activeTab === "Bank"} onClick={() => setActiveTab("Bank")}>
          🏦 Bank {activeType === "Bank" && "✅"}
        </Tab>
        <Tab
          active={activeTab === "Khalti"}
          onClick={() => setActiveTab("Khalti")}
        >
          💜 Khalti {activeType === "Khalti" && "✅"}
        </Tab>
        <Tab
          active={activeTab === "Esewa"}
          onClick={() => setActiveTab("Esewa")}
        >
          💚 eSewa {activeType === "Esewa" && "✅"}
        </Tab>
        <Tab
          active={activeTab === "Fonepay"}
          onClick={() => setActiveTab("Fonepay")}
        >
          💙 Fonepay {activeType === "Fonepay" && "✅"}
        </Tab>
      </TabsContainer>

      <Formik
        initialValues={initialValues[activeTab]}
        validationSchema={
          activeTab === "Bank"
            ? bankValidationSchema
            : digitalWalletValidationSchema
        }
        onSubmit={handleSubmit}
        enableReinitialize
        key={activeTab}
      >
        {({ isSubmitting, setFieldValue }) => (
          <FieldWrapper>
            <Form>
              <FormContainer>
                <LeftSection>
                  {activeTab === "Bank" ? (
                    <>
                      <Fields.Input
                        name="infos.bankName"
                        label="Bank Name"
                        placeholder="Enter bank name"
                      />
                      <Fields.Input
                        name="infos.name"
                        label="Account Holder Name"
                        placeholder="Enter account holder name"
                      />
                      <Fields.Input
                        name="infos.number"
                        label="Account Number"
                        placeholder="Enter account number"
                      />
                      <Fields.Input
                        name="infos.address"
                        label="Bank Address"
                        placeholder="Enter bank address"
                      />
                    </>
                  ) : (
                    <>
                      <Fields.Input
                        name="infos.name"
                        label={`${activeTab} Name`}
                        placeholder={`Enter ${activeTab} name`}
                      />
                      <Fields.Input
                        name="infos.number"
                        label={`${activeTab} Number`}
                        placeholder={`Enter ${activeTab} number`}
                      />
                    </>
                  )}
                </LeftSection>

                <RightSection>
                  <Typography weight={600} size={14}>
                    {activeTab} QR Code
                  </Typography>
                  <Fields.Upload
                    name="qrCode"
                    label=""
                    placeholder="Upload QR Code"
                    variant="banner"
                    useRawFile={true}
                    onChange={(file: any) => setFieldValue("qrCode", file)}
                  />
                </RightSection>
              </FormContainer>

              <ButtonContainer>
                {existingRecords[activeTab] && (
                  <Button
                    type="button"
                    variant="outline"
                    loading={settingActive}
                    disabled={activeType === activeTab}
                    title={
                      activeType === activeTab
                        ? "Currently Active"
                        : "Set Active"
                    }
                    onClick={handleSetActive}
                  />
                )}
                <Button
                  type="submit"
                  loading={loading || isSubmitting}
                  title={`${existingRecords[activeTab] ? "Update" : "Save"} ${activeTab} Details`}
                />
              </ButtonContainer>
            </Form>
          </FieldWrapper>
        )}
      </Formik>
    </Container>
  );
};

export default StoreBankDetails;
